import { Component } from '@theme/component';

/** Native Shopify bundle purchase form with synchronized selectors and guarded submissions. */
class SepetzenPurchase extends Component {
  #controller;
  #busy = false;
  #variants = [];
  #prices = {};
  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#variants = JSON.parse(this.querySelector('[data-sz-variants]')?.textContent || '[]');
    this.#prices = JSON.parse(this.querySelector('[data-sz-formatted-prices]')?.textContent || '{}');
    document.addEventListener('sz:variant-change', event => {
      if (event.detail.product === this.dataset.product && event.detail.source !== this) this.selectVariant(event.detail.id);
    }, { signal: this.#controller.signal });
    const selected = this.querySelector('input[name="id"]:checked');
    if (selected) this.selectVariant(selected.value);
  }
  updatedCallback() { super.updatedCallback(); }
  disconnectedCallback() { this.#controller?.abort(); super.disconnectedCallback(); }
  /** @param {Event} event */
  handleChange(event) {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'id') return;
    this.selectVariant(event.target.value);
    document.dispatchEvent(new CustomEvent('sz:variant-change', {detail:{id:event.target.value,product:this.dataset.product,source:this}}));
    if (window.location.pathname.includes('/products/')) {
      const url = new URL(window.location.href);
      // Preserve existing advertising and preview parameters. The old product URL remains valid.
      if (this.dataset.product === document.querySelector('[data-sz-page-product]')?.dataset.szPageProduct) {
        url.searchParams.set('variant', event.target.value);
        window.history.replaceState({}, '', url);
      }
    }
  }
  selectVariant(id) {
    const variant = this.#variants.find(item => String(item.id) === String(id));
    if (!variant) return;
    for (const input of this.querySelectorAll('input[name="id"]')) {
      input.checked = input.value === String(id);
      input.closest('.sz-package')?.classList.toggle('is-selected', input.checked);
    }
    for (const element of this.querySelectorAll('[data-sz-price]')) element.textContent = this.#prices[id]?.price || '';
    for (const element of this.querySelectorAll('[data-sz-compare]')) {
      element.textContent = this.#prices[id]?.compare || '';
      element.hidden = !(variant.compare_at_price > variant.price);
    }
    for (const element of this.querySelectorAll('[data-sz-selected-name]')) element.textContent = variant.public_title || variant.title;
    for (const button of this.querySelectorAll('[data-sz-submit]')) {
      button.disabled = !variant.available || this.#busy;
      const text = button.querySelector('[data-sz-button-text]');
      if (text) { text.dataset.original ||= text.textContent; text.textContent = variant.available ? text.dataset.original : this.dataset.unavailable; }
    }
  }
  /** @param {SubmitEvent} event */
  async handleSubmit(event) {
    if (!(event.target instanceof HTMLFormElement) || !event.target.classList.contains('sz-product-form')) return;
    event.preventDefault();
    if (this.#busy) return;
    const data = new FormData(event.target);
    const variant = this.#variants.find(item => String(item.id) === String(data.get('id')));
    if (!variant?.available) return;
    this.#busy = true;
    // Lock both purchase sections immediately so a fast second click cannot add twice.
    const components = Array.from(document.querySelectorAll('sz-purchase-component'));
    for (const component of components) component.setBusy(true);
    const status = this.querySelector('[data-sz-status]');
    if (status) status.textContent = this.dataset.adding;
    try {
      const response = await fetch(this.dataset.addUrl, {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items:[{id:variant.id,quantity:1}] }),signal:this.#controller.signal});
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.error);
      if (status) status.textContent = this.dataset.added;
      window.location.assign(this.dataset.cartUrl);
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (status) status.textContent = error.message || this.dataset.error;
      for (const component of components) component.setBusy(false);
      this.querySelector('[data-sz-submit]')?.focus();
    }
  }
  setBusy(value) {
    this.#busy = value;
    const selected = this.querySelector('input[name="id"]:checked');
    for (const button of this.querySelectorAll('[data-sz-submit]')) {
      button.disabled = value;
      button.setAttribute('aria-busy', String(value));
      const text = button.querySelector('[data-sz-button-text]');
      if (text && value) text.textContent = this.dataset.adding;
    }
    if (!value && selected) this.selectVariant(selected.value);
  }
}

/** Customer photo viewer using the browser's accessible native dialog. */
class SepetzenContent extends Component {
  #opener;
  /** @param {MouseEvent} event */
  openPhoto(event) {
    const button = event.target.closest('[data-sz-photo]');
    const dialog = this.refs.photoDialog;
    const image = this.refs.largePhoto;
    if (!(button instanceof HTMLButtonElement) || !(dialog instanceof HTMLDialogElement) || !(image instanceof HTMLImageElement)) return;
    image.src = button.dataset.szPhoto;
    image.alt = button.querySelector('img')?.alt || '';
    this.#opener = button;
    dialog.showModal();
  }
  closePhoto() {
    if (this.refs.photoDialog instanceof HTMLDialogElement) this.refs.photoDialog.close();
    this.#opener?.focus();
  }
}

if (!customElements.get('sz-purchase-component')) customElements.define('sz-purchase-component', SepetzenPurchase);
if (!customElements.get('sz-content-component')) customElements.define('sz-content-component', SepetzenContent);

document.addEventListener('click', event => {
  if (!(event.target instanceof Element) || !event.target.closest('[data-sz-cookie]')) return;
  const banner = window.privacyBanner || window.Shopify?.privacyBanner;
  if (banner?.showPreferences) banner.showPreferences();
  else window.location.assign('/pages/cerez-politikasi');
});

/** Play lightweight product animations only while visible; honor reduced motion. */
class SepetzenMotion extends HTMLElement {
  #observer;
  #controller;
  #activated = false;
  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const video = this.querySelector('video');
    if (!video) return;
    const playButton = this.querySelector('button');
    video.addEventListener('playing', () => {
      video.hidden = false;
      if (playButton) playButton.hidden = true;
    }, { signal: this.#controller.signal });
    playButton?.addEventListener('click', () => {
      this.#activated = true;
      video.hidden = false;
      video.muted = true;
      video.play().catch(() => { /* Native controls remain available. */ });
    }, { signal: this.#controller.signal });
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const observe = () => {
      this.#observer?.disconnect();
      video.pause();
      if (motion.matches || navigator.connection?.saveData) return;
      this.#observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (this.#activated && entry.isIntersecting && !document.hidden) {
            video.muted = true;
            video.play().catch(() => { /* Native controls remain available. */ });
          } else video.pause();
        }
      }, { threshold: 0.15 });
      this.#observer.observe(this);
    };
    // Paint the cover first; non-essential motion starts after visitor interaction.
    const activate = () => {
      if (this.#activated) return;
      this.#activated = true;
      observe();
    };
    for (const type of ['pointerdown', 'keydown', 'touchstart', 'wheel']) {
      document.addEventListener(type, activate, { capture: true, passive: true, once: true, signal: this.#controller.signal });
    }
    motion.addEventListener('change', observe, { signal: this.#controller.signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) video.pause();
      else observe();
    }, { signal: this.#controller.signal });
    observe();
  }
  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
    this.querySelector('video')?.pause();
  }
}
if (!customElements.get('sz-motion-component')) customElements.define('sz-motion-component', SepetzenMotion);
